// src/pages/ReferencesPage.js
import React from 'react';
import { Box, Typography, Link as MuiLink, CardMedia } from '@mui/material';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import HeroBanner from '../components/HeroBanner';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import ReactMarkdown from 'react-markdown';
import { refItems } from '../data/SiteData';

const ReferencesPage = () => {
  const { refId } = useParams();

  // Find the reference based on the ID in the URL
  const reference = refItems.find((item) => item.refId === refId);
  if (!reference) return <Typography>Reference not found.</Typography>;

  // Breadcrumb items
  const breadcrumbItems = [
    { title: 'Home', path: '/' },
    { title: 'Resources', path: '/resources' },
    { title: 'References', path: '/resources/references' },
    {
      title:
        reference.title.length > 30
          ? reference.title.substring(0, 30) + '...'
          : reference.title,
      path: reference.link,
    },
  ];

  const formattedDate = reference.date || '';

  return (
    <>
      <Navbar />
      <HeroBanner
        heroTitle="References"
        heroDescription="Explore all educational and scientific references."
        imageUrl="/images/digiTwins.png"
        height="165px"
      />
      <Breadcrumb items={breadcrumbItems} />

      {/* Main content */}
      <Box sx={{ padding: '32px', maxWidth: '1300px', margin: '0 auto' }}>
        {/* First section */}
        <Box sx={{ display: 'flex', gap: 4, mb: 4, flexWrap: 'wrap' }}>
          {reference.image && (
            <CardMedia
              component="img"
              src={reference.image}
              alt={reference.title}
              sx={{ width: 160, flexShrink: 0, borderRadius: '8px' }}
            />
          )}

          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                fontSize: '24px',
                color: '#142434',
                mb: 1,
              }}
            >
              {reference.title}
            </Typography>

            <Typography
              sx={{
                fontFamily: 'PT Sans, sans-serif',
                fontSize: '14px',
                color: '#142434',
                mb: 1,
              }}
            >
              {formattedDate}
            </Typography>

            <Typography
              sx={{
                fontFamily: 'PT Sans, sans-serif',
                fontSize: '14px',
                color: '#142434',
                mb: 1,
                textTransform: 'uppercase',
              }}
            >
              {reference.refType}
            </Typography>

            {reference.extLink && (
              <MuiLink
                href={reference.extLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontFamily: 'PT Sans, sans-serif',
                  fontSize: 14,
                  wordBreak: 'break-all',
                }}
              >
                {reference.extLink}
              </MuiLink>
            )}
          </Box>
        </Box>

        {/* Second section: mainContent */}
        {(reference.mainContent || reference.description) && (
          <Box
            sx={{
              mt: 4,
              fontFamily: 'PT Sans, sans-serif',
              fontSize: '16px',
              lineHeight: 1.6,
              color: '#142434',
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                fontFamily: 'Poppins, sans-serif',
              },
              '& a': {
                color: '#489596',
                textDecoration: 'underline',
              },
            }}
          >
            <ReactMarkdown>
              {reference.mainContent ? reference.mainContent : reference.description}
            </ReactMarkdown>
          </Box>
        )}
      </Box>

      <Footer />
    </>
  );
};

export default ReferencesPage;
